import { adminApiV2 } from '@/lib/admin-api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface TransactionFilters {
  query?: string
  user_id?: string
  product?: string
  rail?: string
  status?: string
  internal_status?: string
  currency?: string
  direction?: string
  recon_badge?: string
  kyc_status?: string
  country_code?: string
  from_date?: string
  to_date?: string
  limit?: number
  after_cursor?: string

  // Phase 2: Entity filtering
  entity_type?: 'liquidation' | 'payout' | 'return' | 'transfer' | 'deposit' | 'withdrawal' | ''
  liquidation_address_id?: string
  external_account_id?: string
  return_wallet_id?: string
  amount_min?: number
  amount_max?: number
}

export interface Transaction {
  id: string
  created_at: string
  user_id: string
  customer_name?: string
  product: string
  rail: string
  amount: number
  currency: string
  status: string
  internal_status?: string
  country_code?: string
  recon_badge?: string
  kyc_status?: string
  direction: string

  // Phase 2: Entity relationships
  entity_type?: 'liquidation' | 'payout' | 'return' | 'transfer' | 'deposit' | 'withdrawal'
  entity_id?: string
  liquidation_address_id?: string
  external_account_id?: string
  return_wallet_id?: string
  wallet_id?: string

  // Additional metadata for entity display
  liquidation_address?: string
  external_account_masked?: string
  return_wallet_address?: string
  blockchain_tx_hash?: string
  network_confirmations?: number
}

export interface TransactionsResponse {
  transactions: Transaction[]
  nextCursor?: string
  hasMore: boolean
  total?: number
}

export function useTransactions(initialFilters: TransactionFilters = {}) {
  const [data, setData] = useState<Transaction[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [filters, setFilters] = useState<TransactionFilters>(initialFilters)
  const [nextCursor, setNextCursor] = useState<string | undefined>()
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState<number | undefined>()

  // Use ref to store initial filters and prevent infinite loops
  const initialFiltersRef = useRef<TransactionFilters>(initialFilters)
  const hasInitializedRef = useRef(false)

  const fetchTransactions = useCallback(async (params: TransactionFilters = {}, append = false) => {
    setIsLoading(true)
    setError(null)

    try {
      // Map UI filters to RPC parameters
      // Map product filter to kind_filter
      const mapProductToKind = (product?: string): string[] | undefined => {
        if (!product || product === 'all') return undefined;
        const mapping: Record<string, string[]> = {
          'liquidation': ['drain'],
          'payout': ['payout'],
          'deposit': ['va_deposit'],
          'va': ['va_deposit'],
          'transfer': ['transfer'],
          'return': ['return'],
        };
        return mapping[product];
      };

      // Map entity_type filter to kind_filter
      const mapEntityTypeToKind = (entityType?: string): string[] | undefined => {
        if (!entityType || entityType === 'all' || entityType === '') return undefined;
        const mapping: Record<string, string[]> = {
          'liquidation': ['drain'],
          'payout': ['payout'],
          'deposit': ['va_deposit'],
          'withdrawal': ['payout'],
          'transfer': ['transfer'],
          'return': ['return'],
        };
        return mapping[entityType];
      };

      // Combine product and entity_type filters into kind_filter
      const productKinds = mapProductToKind(params.product);
      const entityKinds = mapEntityTypeToKind(params.entity_type);
      const kindFilter = productKinds || entityKinds;

      // Map status filter (UI uses 'completed', RPC uses 'settled')
      const mapStatusToRpc = (status?: string): string[] | undefined => {
        if (!status || status === 'all') return undefined;
        const mapping: Record<string, string> = {
          'completed': 'settled',
          'pending': 'pending',
          'failed': 'failed',
          'cancelled': 'cancelled',
        };
        const rpcStatus = mapping[status] || status;
        return [rpcStatus];
      };

      // Extract cursor from after_cursor (format: "timestamp|uuid")
      let cursor_created_at: string | undefined;
      let cursor_id: string | undefined;
      if (params.after_cursor) {
        const parts = params.after_cursor.split('|');
        if (parts.length === 2) {
          cursor_created_at = parts[0];
          cursor_id = parts[1];
        }
      }

      const rpcParams = {
        customer_filter: params.query || params.user_id,
        kind_filter: kindFilter,
        status_filter: mapStatusToRpc(params.status),
        currency_filter: params.currency && params.currency !== 'all' ? params.currency : undefined,
        amount_min: params.amount_min,
        amount_max: params.amount_max,
        from_date: params.from_date,
        to_date: params.to_date,
        limit_param: params.limit || 50,
        cursor_created_at: cursor_created_at,
        cursor_id: cursor_id,
        // Filters for entity specific search
        liquidation_address_filter: params.liquidation_address_id,
        external_account_filter: params.external_account_id,
      };

      // Use AdminApiClient V2 for type-safe RPC
      const result = await adminApiV2.call('ops.transactions.list', rpcParams);

      if (!result.ok) {
        throw new Error(result.error || 'Failed to fetch transactions');
      }

      // Parse JSONB response: {data: [...], total: number, hasMore: boolean}
      // AdminApiClient already extracts data from response
      const response = result.data as { data?: any[]; total?: number; hasMore?: boolean };

      // Handle both structure formats (direct array or wrapped object)
      const transactionsRaw = (Array.isArray(response) ? response : (response?.data || [])) as any[];
      const totalCount = (response && !Array.isArray(response)) ? (response.total ?? 0) : transactionsRaw.length;
      const hasMoreFromRpc = (response && !Array.isArray(response)) ? (response.hasMore ?? false) : false;

      // Helper function to determine product from kind
      const getProductFromKind = (kind: string): string => {
        switch (kind) {
          case 'va_deposit':
            return 'va'
          case 'drain':
            return 'liquidation'
          case 'payout':
            return 'payout'
          case 'transfer':
            return 'transfer'
          case 'return':
            return 'return'
          default:
            return kind // Fallback to kind if unknown
        }
      }

      // Helper function to determine direction from kind and amounts
      const getDirectionFromKind = (kind: string, amountIn?: number, amountOut?: number): string => {
        // Incoming transactions
        if (kind === 'va_deposit' || kind === 'drain') {
          return 'in'
        }
        // Outgoing transactions
        if (kind === 'payout' || kind === 'return') {
          return 'out'
        }
        // For transfers, analyze amounts
        if (kind === 'transfer') {
          if (amountIn && amountIn > 0 && (!amountOut || amountOut === 0)) {
            return 'in'
          }
          return 'out'
        }
        // Fallback: analyze amounts
        if (amountIn && amountIn > 0 && (!amountOut || amountOut === 0)) {
          return 'in'
        }
        return 'out'
      }

      // Helper function to determine rail from kind and assets
      const getRailFromKind = (kind: string, assetIn?: string, assetOut?: string): string => {
        // Drain operations are always crypto
        if (kind === 'drain') {
          return 'crypto'
        }
        // Check assets for crypto indicators
        const cryptoAssets = ['BTC', 'ETH', 'USDC', 'USDT', 'WBTC', 'DAI']
        const asset = assetOut || assetIn || ''
        if (cryptoAssets.some(crypto => asset.toUpperCase().includes(crypto))) {
          return 'crypto'
        }
        // For va_deposit and payout, default to wire (could be improved with metadata)
        if (kind === 'va_deposit' || kind === 'payout') {
          return 'wire' // Default assumption, could be 'ach' - needs metadata
        }
        // For transfers, check both assets
        if (kind === 'transfer') {
          return cryptoAssets.some(crypto =>
            (assetIn || '').toUpperCase().includes(crypto) ||
            (assetOut || '').toUpperCase().includes(crypto)
          ) ? 'crypto' : 'wire'
        }
        // Fallback
        return 'wire'
      }

      // Helper function to map status
      const mapStatus = (status: string): string => {
        switch (status) {
          case 'settled':
            return 'completed'
          case 'failed':
            return 'failed'
          case 'cancelled':
            return 'cancelled'
          case 'pending':
          case 'confirmed':
            return 'pending'
          case 'created':
            return 'pending'
          default:
            return status // Return as-is if unknown
        }
      }

      // Map RPC response to Transaction interface with proper data mapping
      const mappedTransactions: Transaction[] = transactionsRaw.map(tx => {
        // Extract metadata if available
        const metadata = tx.bridge_ids || {}

        return {
          id: tx.id,
          created_at: tx.created_at,
          user_id: tx.user_id,
          customer_name: tx.customer_name || tx.customer_email || tx.user_id,
          product: getProductFromKind(tx.kind),
          rail: getRailFromKind(tx.kind, tx.asset_in, tx.asset_out),
          amount: tx.amount_out || tx.amount_in || 0,
          currency: tx.asset_out || tx.asset_in || 'USD',
          status: mapStatus(tx.status),
          internal_status: tx.status,
          country_code: undefined, // TODO: Add to RPC response
          recon_badge: undefined, // TODO: Extract from metadata or compute
          kyc_status: tx.customer_kyc_status,
          direction: getDirectionFromKind(tx.kind, tx.amount_in, tx.amount_out),
          entity_type: (() => {
            // Map kind to entity_type
            switch (tx.kind) {
              case 'drain': return 'liquidation';
              case 'va_deposit': return 'deposit';
              case 'payout': return 'payout';
              case 'return': return 'return';
              case 'transfer': return 'transfer';
              default: return tx.kind as any;
            }
          })(),
          entity_id: tx.id,
          liquidation_address_id: tx.liquidation_address_id || undefined,
          external_account_id: tx.external_account_id || undefined,
        }
      })

      // Debug logging removed for production-ready code

      if (append) {
        setData(prev => [...prev, ...mappedTransactions])
      } else {
        setData(mappedTransactions)
      }

      // Check if there are more results (cursor-based pagination)
      const lastTx = transactionsRaw.length > 0 ? transactionsRaw[transactionsRaw.length - 1] : null;
      const hasMoreResults = hasMoreFromRpc || (transactionsRaw.length === (params.limit || 50) && lastTx !== null);

      // Build cursor string: "timestamp|uuid"
      const cursor = hasMoreResults && lastTx?.next_cursor_created_at && lastTx?.next_cursor_id
        ? `${lastTx.next_cursor_created_at}|${lastTx.next_cursor_id}`
        : undefined;

      setNextCursor(cursor)
      setHasMore(hasMoreResults)
      // Use total count from RPC response
      setTotal(totalCount)

    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'))
      if (!append) {
        setData([])
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Stabilize initialFilters with useMemo to prevent unnecessary re-fetches
  const stableInitialFilters = useMemo(() => initialFilters, [
    initialFilters.query,
    initialFilters.user_id,
    initialFilters.status,
    initialFilters.currency,
    initialFilters.limit,
    initialFilters.from_date,
    initialFilters.to_date,
  ])

  // Initialize filters only once on mount, or when initialFilters actually changes
  useEffect(() => {
    // Only fetch on first mount or when stableInitialFilters reference changes
    if (!hasInitializedRef.current) {
      initialFiltersRef.current = stableInitialFilters
      setFilters(stableInitialFilters)
      fetchTransactions(stableInitialFilters)
      hasInitializedRef.current = true
    } else {
      // Compare only if filters actually changed
      const currentStr = JSON.stringify(initialFiltersRef.current)
      const newStr = JSON.stringify(stableInitialFilters)
      if (currentStr !== newStr) {
        initialFiltersRef.current = stableInitialFilters
        setFilters(stableInitialFilters)
        fetchTransactions(stableInitialFilters)
      }
    }
     
  }, [stableInitialFilters, fetchTransactions])

  const updateFilters = useCallback((newFilters: Partial<TransactionFilters>) => {
    const updatedFilters = { ...filters, ...newFilters }
    setFilters(updatedFilters)
    fetchTransactions(updatedFilters)
  }, [filters, fetchTransactions])

  const loadMore = useCallback(() => {
    if (hasMore && nextCursor && !isLoading) {
      fetchTransactions({ ...filters, after_cursor: nextCursor }, true)
    }
  }, [hasMore, nextCursor, isLoading, filters, fetchTransactions])

  const refresh = useCallback(() => {
    fetchTransactions(filters)
  }, [filters, fetchTransactions])

  const exportData = useCallback(async (format: 'csv' | 'json') => {
    try {
      // Map UI filters to RPC parameters (same logic as fetchTransactions)
      const mapProductToKind = (product?: string): string[] | undefined => {
        if (!product || product === 'all') return undefined;
        const mapping: Record<string, string[]> = {
          'liquidation': ['drain'],
          'payout': ['payout'],
          'deposit': ['va_deposit'],
          'va': ['va_deposit'],
          'transfer': ['transfer'],
          'return': ['return'],
        };
        return mapping[product];
      };

      const mapEntityTypeToKind = (entityType?: string): string[] | undefined => {
        if (!entityType || entityType === 'all' || entityType === '') return undefined;
        const mapping: Record<string, string[]> = {
          'liquidation': ['drain'],
          'payout': ['payout'],
          'deposit': ['va_deposit'],
          'withdrawal': ['payout'],
          'transfer': ['transfer'],
          'return': ['return'],
        };
        return mapping[entityType];
      };

      const productKinds = mapProductToKind(filters.product);
      const entityKinds = mapEntityTypeToKind(filters.entity_type);
      const kindFilter = productKinds || entityKinds;

      const mapStatusToRpc = (status?: string): string[] | undefined => {
        if (!status || status === 'all') return undefined;
        const mapping: Record<string, string> = {
          'completed': 'settled',
          'pending': 'pending',
          'failed': 'failed',
          'cancelled': 'cancelled',
        };
        const rpcStatus = mapping[status] || status;
        return [rpcStatus];
      };

      // Fetch all transactions for export (large limit)
      const rpcParams = {
        customer_filter: filters.query || filters.user_id,
        kind_filter: kindFilter,
        status_filter: mapStatusToRpc(filters.status),
        currency_filter: filters.currency && filters.currency !== 'all' ? filters.currency : undefined,
        amount_min: filters.amount_min,
        amount_max: filters.amount_max,
        from_date: filters.from_date,
        to_date: filters.to_date,
        limit_param: 10000, // Large limit for export
      };

      // Use AdminApiClient V2 for type-safe RPC
      const result = await adminApiV2.call('ops.transactions.list', rpcParams);

      if (!result.ok) {
        throw new Error(result.error || 'Failed to fetch transactions for export');
      }

      const response = result.data as { data?: any[] };
      const transactions = (Array.isArray(response) ? response : (response?.data || [])) as any[];

      let exportContent: string
      let mimeType: string
      let fileExtension: string

      if (format === 'csv') {
        // Generate CSV
        const headers = ['ID', 'Created At', 'Customer', 'Type', 'Amount', 'Currency', 'Status', 'KYC Status']
        const rows = transactions.map(tx => [
          tx.id,
          tx.created_at,
          tx.customer_email || tx.customer_name || '',
          tx.kind,
          tx.amount_out || 0,
          tx.asset_out || '',
          tx.status,
          tx.customer_kyc_status || ''
        ])
        exportContent = [headers, ...rows].map(row => row.join(',')).join('\n')
        mimeType = 'text/csv'
        fileExtension = 'csv'
      } else {
        // Generate JSON
        exportContent = JSON.stringify(transactions, null, 2)
        mimeType = 'application/json'
        fileExtension = 'json'
      }

      // Create download
      const blob = new Blob([exportContent], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transactions-${new Date().toISOString().split('T')[0]}.${fileExtension}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

    } catch (err) {
      throw err instanceof Error ? err : new Error(`Export ${format} failed`)
    }
  }, [filters])

  // KPIs derived from data
  const kpis = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]
    const todayTransactions = data.filter(tx =>
      tx.created_at.startsWith(today)
    )
    const failedToday = todayTransactions.filter(tx =>
      tx.status === 'failed' || tx.internal_status === 'failed'
    )

    const aggregateMetrics = (transactions: Transaction[]) => {
      const amountsByCurrency = transactions.reduce<Record<string, number>>((acc, tx) => {
        const currency = (tx.currency || 'USD').toUpperCase()
        const amount = tx.amount || 0
        acc[currency] = (acc[currency] || 0) + amount
        return acc
      }, {})

      const orderedAmounts: Array<[string, number]> = Object.entries(amountsByCurrency)
        .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))

      return {
        count: transactions.length,
        amounts: orderedAmounts,
      }
    }

    const formatCurrency = (amount: number, currency: string) => {
      const normalizedCurrency = (currency || 'USD').toUpperCase()
      const safeAmount = Number.isFinite(amount) ? amount : 0

      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: normalizedCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(safeAmount)
      } catch {
        const decimalFormatter = new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
        return `${normalizedCurrency} ${decimalFormatter.format(safeAmount)}`
      }
    }

    const formatAmountsSummary = (amounts: Array<[string, number]>) => {
      if (!amounts.length) {
        return formatCurrency(0, 'USD')
      }

      if (amounts.length === 1) {
        const [currency, amount] = amounts[0]
        return formatCurrency(amount, currency)
      }

      return amounts
        .map(([currency, amount]) => formatCurrency(amount, currency))
        .join(' · ')
    }

    const formatStatValue = (
      count: number,
      amounts: Array<[string, number]>,
      overrideCount?: number
    ) => {
      const countText = (overrideCount ?? count).toLocaleString()
      const amountText = formatAmountsSummary(amounts)
      return `${countText} • ${amountText}`
    }

    const totalAggregate = aggregateMetrics(data)
    const todayAggregate = aggregateMetrics(todayTransactions)
    const liquidationsAggregate = aggregateMetrics(data.filter(tx => tx.entity_type === 'liquidation'))
    const payoutsAggregate = aggregateMetrics(data.filter(tx => tx.entity_type === 'payout'))
    const returnsAggregate = aggregateMetrics(data.filter(tx => tx.entity_type === 'return'))
    const failedAggregate = aggregateMetrics(failedToday)

    return [
      {
        label: 'Total',
        value: formatStatValue(
          totalAggregate.count,
          totalAggregate.amounts,
          total ?? totalAggregate.count
        ),
        variant: 'default' as const,
      },
      {
        label: 'Today',
        value: formatStatValue(todayAggregate.count, todayAggregate.amounts),
        variant: 'default' as const,
      },
      {
        label: 'Liquidations',
        value: formatStatValue(liquidationsAggregate.count, liquidationsAggregate.amounts),
        variant: 'default' as const,
      },
      {
        label: 'Payouts',
        value: formatStatValue(payoutsAggregate.count, payoutsAggregate.amounts),
        variant: 'default' as const,
      },
      {
        label: 'Returns',
        value: formatStatValue(returnsAggregate.count, returnsAggregate.amounts),
        variant: 'default' as const,
      },
      {
        label: 'Failed Today',
        value: formatStatValue(failedAggregate.count, failedAggregate.amounts),
        variant: failedToday.length > 0 ? ('destructive' as const) : ('success' as const),
      },
      {
        label: 'Success Rate',
        value: todayTransactions.length > 0
          ? `${Math.round((1 - failedToday.length / todayTransactions.length) * 100)}%`
          : '100%',
        variant: 'default' as const,
      },
    ]
  }, [data, total])

  return {
    data,
    isLoading,
    error,
    filters,
    updateFilters,
    refresh,
    loadMore,
    hasMore,
    nextCursor,
    exportData,
    kpis,
    total
  }
}